// src/pages/UserPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Typography, 
  Row, 
  Col, 
  Button, 
  Space, 
  Table, 
  Tag, 
  Input, 
  Select, 
  Modal, 
  Form,
  Avatar,
  Popconfirm,
  Tooltip,
  Divider,
  Switch,
  message,
  Alert,
  Pagination
} from 'antd';
import { 
  PlusOutlined, 
  SearchOutlined, 
  UserOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SettingOutlined,
  SecurityScanOutlined,
  LeftOutlined,
  RightOutlined,
  KeyOutlined,
  CrownOutlined,
  UserSwitchOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

const UserPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [filteredProfiles, setFilteredProfiles] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isRoleModalVisible, setIsRoleModalVisible] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [form] = Form.useForm();
  const [roleForm] = Form.useForm();

  // Filtres
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    status: ''
  });

  // Statistiques
  const [stats, setStats] = useState({
    total: 0,
    admins: 0,
    managers: 0,
    users: 0,
    active: 0,
    inactive: 0
  });

  // Permissions disponibles
  const availablePermissions = [
    { key: 'dashboard', label: 'Tableau de bord' },
    { key: 'clients', label: 'Gestion clients' },
    { key: 'vehicules', label: 'Gestion véhicules' },
    { key: 'vente', label: 'Ventes' },
    { key: 'achat', label: 'Achats' },
    { key: 'fournisseurs', label: 'Fournisseurs' },
    { key: 'personnels', label: 'Personnel' },
    { key: 'reports', label: 'Rapports' },
    { key: 'settings', label: 'Paramètres' },
    { key: 'profile', label: 'Gestion profils' }
  ];

  // Rôles disponibles
  const roleOptions = [
    { value: 'admin', label: 'Administrateur', color: 'red' },
    { value: 'manager', label: 'Manager', color: 'orange' },
    { value: 'user', label: 'Utilisateur', color: 'blue' },
    { value: 'viewer', label: 'Observateur', color: 'green' }
  ];

  const statusOptions = ['Actif', 'Inactif', 'Suspendu'];

  // Données initiales (mock)
  const initialProfiles = [
    { 
      id: 'U001', 
      username: 'admin', 
      email: 'admin@latyr.com',
      fullName: 'Administrateur Principal',
      role: 'admin',
      status: 'Actif',
      permissions: ['dashboard', 'clients', 'vehicules', 'vente', 'achat', 'fournisseurs', 'personnels', 'reports', 'settings', 'profile'],
      lastLogin: '2024-01-25 14:30:00',
      createdAt: '2023-01-01',
      phone: '+221 77 123 45 67',
      department: 'Administration'
    },
    { 
      id: 'U002', 
      username: 'manager1', 
      email: 'manager@latyr.com',
      fullName: 'Manager Commercial',
      role: 'manager',
      status: 'Actif',
      permissions: ['dashboard', 'clients', 'vente', 'reports'],
      lastLogin: '2024-01-24 09:15:00',
      createdAt: '2023-02-15',
      phone: '+221 78 234 56 78',
      department: 'Commercial'
    },
    { 
      id: 'U003', 
      username: 'user1', 
      email: 'user@latyr.com',
      fullName: 'Agent Logistique',
      role: 'user',
      status: 'Actif',
      permissions: ['dashboard', 'vehicules', 'achat'],
      lastLogin: '2024-01-23 16:45:00',
      createdAt: '2023-03-10',
      phone: '+221 76 345 67 89',
      department: 'Logistique'
    },
    { 
      id: 'U004', 
      username: 'viewer1', 
      email: 'viewer@latyr.com',
      fullName: 'Auditeur',
      role: 'viewer',
      status: 'Inactif',
      permissions: ['dashboard', 'reports'],
      lastLogin: '2023-12-05 11:20:00',
      createdAt: '2023-04-20',
      phone: '+221 70 456 78 90',
      department: 'Audit'
    },
  ];

  useEffect(() => {
    fetchProfiles();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, profiles]);

  const fetchProfiles = () => {
    setLoading(true);
    // Simulation API call
    setTimeout(() => {
      setProfiles(initialProfiles);
      setFilteredProfiles(initialProfiles);
      calculateStats(initialProfiles);
      setLoading(false);
    }, 500);
  };

  const calculateStats = (profilesArray) => {
    const total = profilesArray.length;
    const admins = profilesArray.filter(p => p.role === 'admin').length;
    const managers = profilesArray.filter(p => p.role === 'manager').length;
    const users = profilesArray.filter(p => p.role === 'user').length;
    const active = profilesArray.filter(p => p.status === 'Actif').length;
    const inactive = profilesArray.filter(p => p.status !== 'Actif').length;
    
    setStats({ total, admins, managers, users, active, inactive });
  };

  const applyFilters = () => {
    let filtered = [...profiles];

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(profile => 
        profile.username.toLowerCase().includes(searchLower) ||
        profile.fullName.toLowerCase().includes(searchLower) ||
        profile.email.toLowerCase().includes(searchLower)
      );
    }

    if (filters.role) {
      filtered = filtered.filter(profile => profile.role === filters.role);
    }

    if (filters.status) {
      filtered = filtered.filter(profile => profile.status === filters.status);
    }

    setFilteredProfiles(filtered);
    calculateStats(filtered);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const showModal = (profile = null) => {
    setEditingProfile(profile);
    if (profile) {
      form.setFieldsValue(profile);
    } else {
      form.resetFields();
    }
    setIsModalVisible(true);
  };

  const showRoleModal = (profile) => {
    setEditingProfile(profile);
    roleForm.setFieldsValue({
      role: profile.role,
      permissions: profile.permissions
    });
    setIsRoleModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingProfile(null);
    form.resetFields();
  };

  const handleRoleCancel = () => {
    setIsRoleModalVisible(false);
    setEditingProfile(null);
    roleForm.resetFields();
  };

  const handleSubmit = (values) => {
    setLoading(true);
    
    setTimeout(() => {
      if (editingProfile) {
        // Mise à jour
        const updatedProfiles = profiles.map(profile => 
          profile.id === editingProfile.id ? { ...profile, ...values } : profile
        );
        setProfiles(updatedProfiles);
        message.success('Profil modifié avec succès!');
      } else {
        // Création
        const newId = `U${(profiles.length + 101).toString().padStart(3, '0')}`;
        const newProfile = {
          id: newId,
          ...values,
          permissions: values.role === 'admin' ? availablePermissions.map(p => p.key) : [],
          status: 'Actif',
          createdAt: new Date().toISOString().split('T')[0],
          lastLogin: null
        };
        setProfiles([...profiles, newProfile]);
        message.success('Profil créé avec succès!');
      }
      
      setLoading(false);
      setIsModalVisible(false);
      form.resetFields();
    }, 500);
  };

  const handleRoleSubmit = (values) => {
    setLoading(true);
    
    setTimeout(() => {
      const updatedProfiles = profiles.map(profile => 
        profile.id === editingProfile.id ? { 
          ...profile, 
          role: values.role,
          permissions: values.permissions || []
        } : profile
      );
      
      setProfiles(updatedProfiles);
      message.success('Rôle et permissions mis à jour!');
      setLoading(false);
      setIsRoleModalVisible(false);
      roleForm.resetFields();
    }, 500);
  };

  const handleDelete = (profile) => {
    setLoading(true);
    
    setTimeout(() => {
      // Empêcher la suppression de l'admin principal
      if (profile.username === 'admin') {
        message.error('Impossible de supprimer le compte administrateur principal');
        setLoading(false);
        return;
      }
      
      const filtered = profiles.filter(p => p.id !== profile.id);
      setProfiles(filtered);
      message.success('Profil supprimé avec succès!');
      setLoading(false);
    }, 300);
  };

  const handleStatusToggle = (profile) => {
    const newStatus = profile.status === 'Actif' ? 'Inactif' : 'Actif';
    const updatedProfiles = profiles.map(p => 
      p.id === profile.id ? { ...p, status: newStatus } : p
    );
    
    setProfiles(updatedProfiles);
    message.success(`Statut changé à ${newStatus}`);
  };

  const renderRoleBadge = (role) => {
    const roleConfig = roleOptions.find(r => r.value === role);
    return (
      <Tag 
        color={roleConfig?.color || 'default'} 
        className="font-medium flex items-center gap-1"
      >
        {role === 'admin' && <CrownOutlined />}
        {roleConfig?.label || role}
      </Tag>
    );
  };

  const renderStatusBadge = (status) => {
    switch (status) {
      case 'Actif':
        return <Tag color="green" icon={<CheckCircleOutlined />} className="font-medium">Actif</Tag>;
      case 'Inactif':
        return <Tag color="red" icon={<CloseCircleOutlined />} className="font-medium">Inactif</Tag>;
      case 'Suspendu':
        return <Tag color="orange" className="font-medium">Suspendu</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const renderPermissions = (permissions) => {
    return (
      <div className="flex flex-wrap gap-1">
        {permissions.slice(0, 3).map(perm => (
          <Tag key={perm} color="blue" size="small">
            {perm}
          </Tag>
        ))}
        {permissions.length > 3 && (
          <Tag color="default" size="small">
            +{permissions.length - 3}
          </Tag>
        )}
      </div>
    );
  };

  const getRolePermissions = (role) => {
    switch (role) {
      case 'admin':
        return availablePermissions.map(p => p.key);
      case 'manager':
        return ['dashboard', 'clients', 'vente', 'reports'];
      case 'user':
        return ['dashboard', 'vehicules', 'achat'];
      case 'viewer':
        return ['dashboard', 'reports'];
      default:
        return [];
    }
  };

  const columns = [
    {
      title: 'Utilisateur',
      key: 'user',
      width: 200,
      render: (_, record) => (
        <div className="flex items-center">
          <Avatar 
            size="large"
            className="bg-blue-100 text-blue-600 mr-3"
            icon={<UserOutlined />}
          />
          <div>
            <Text strong className="block">{record.fullName}</Text>
            <Text type="secondary" className="text-xs">
              @{record.username}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_, record) => (
        <div>
          <Text className="block text-sm">{record.email}</Text>
          {record.phone && (
            <Text type="secondary" className="text-xs">{record.phone}</Text>
          )}
        </div>
      ),
    },
    {
      title: 'Rôle',
      dataIndex: 'role',
      key: 'role',
      width: 120,
      render: renderRoleBadge,
    },
    {
      title: 'Permissions',
      dataIndex: 'permissions',
      key: 'permissions',
      width: 150,
      render: renderPermissions,
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status, record) => (
        <div className="flex items-center gap-2">
          {renderStatusBadge(status)}
          <Switch
            size="small"
            checked={status === 'Actif'}
            onChange={() => handleStatusToggle(record)}
            disabled={record.username === 'admin'}
          />
        </div>
      ),
    },
    {
      title: 'Dernière connexion',
      dataIndex: 'lastLogin',
      key: 'lastLogin',
      width: 140,
      render: (date) => (
        <Text type="secondary" className="text-xs">
          {date ? dayjs(date).format('DD/MM/YY HH:mm') : 'Jamais'}
        </Text>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Voir détails">
            <Button 
              type="link" 
              icon={<EyeOutlined />} 
              onClick={() => showModal(record)}
              className="text-blue-500"
            />
          </Tooltip>
          <Tooltip title="Gérer rôles">
            <Button 
              type="link" 
              icon={<UserSwitchOutlined />} 
              onClick={() => showRoleModal(record)}
              className="text-purple-500"
            />
          </Tooltip>
          <Tooltip title="Modifier">
            <Button 
              type="link" 
              icon={<EditOutlined />} 
              onClick={() => showModal(record)}
              className="text-green-500"
            />
          </Tooltip>
          <Popconfirm 
            title="Supprimer ce profil ?"
            description="Cette action est irréversible. Êtes-vous sûr ?"
            onConfirm={() => handleDelete(record)}
            okText="Oui"
            cancelText="Non"
            okType="danger"
            disabled={record.username === 'admin'}
          >
            <Tooltip title={record.username === 'admin' ? "Impossible de supprimer" : "Supprimer"}>
              <Button 
                type="link" 
                icon={<DeleteOutlined />} 
                className="text-red-500"
                disabled={record.username === 'admin'}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const paginatedData = filteredProfiles.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  return (
    <div>
      {/* En-tête */}
      <div className="mb-6">
        <Title level={2} className="m-0">Gestion des Profils Utilisateurs</Title>
        <Text className="text-gray-600 mt-2 block">
          Gérez les profils, rôles et permissions des utilisateurs de l'application
        </Text>
      </div>

      {/* Statistiques */}
      <div className="mb-6">
        <Row gutter={[8, 8]}>
          <Col xs={6} sm={6} lg={4}>
            <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm text-center">
              <div className="mb-2">
                <TeamOutlined className="text-blue-500 text-2xl" />
              </div>
              <Text className="text-gray-500 text-sm block">Total</Text>
              <Text className="text-2xl font-bold block">{stats.total}</Text>
            </div>
          </Col>
          <Col xs={6} sm={6} lg={4}>
            <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm text-center">
              <div className="mb-2">
                <CrownOutlined className="text-red-500 text-2xl" />
              </div>
              <Text className="text-gray-500 text-sm block">Administrateurs</Text>
              <Text className="text-2xl font-bold text-red-600 block">{stats.admins}</Text>
            </div>
          </Col>
          <Col xs={6} sm={6} lg={4}>
            <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm text-center">
              <div className="mb-2">
                <UserSwitchOutlined className="text-orange-500 text-2xl" />
              </div>
              <Text className="text-gray-500 text-sm block">Managers</Text>
              <Text className="text-2xl font-bold text-orange-600 block">{stats.managers}</Text>
            </div>
          </Col>
          <Col xs={6} sm={6} lg={4}>
            <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm text-center">
              <div className="mb-2">
                <UserOutlined className="text-green-500 text-2xl" />
              </div>
              <Text className="text-gray-500 text-sm block">Actifs</Text>
              <Text className="text-2xl font-bold text-green-600 block">{stats.active}</Text>
            </div>
          </Col>
          <Col xs={6} sm={6} lg={4}>
            <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm text-center">
              <div className="mb-2">
                <SafetyCertificateOutlined className="text-purple-500 text-2xl" />
              </div>
              <Text className="text-gray-500 text-sm block">Permissions</Text>
              <Text className="text-2xl font-bold text-purple-600 block">
                {availablePermissions.length}
              </Text>
            </div>
          </Col>
          <Col xs={6} sm={6} lg={4}>
            <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm text-center">
              <div className="mb-2">
                <SecurityScanOutlined className="text-gray-500 text-2xl" />
              </div>
              <Text className="text-gray-500 text-sm block">Niveaux rôles</Text>
              <Text className="text-2xl font-bold text-gray-600 block">{roleOptions.length}</Text>
            </div>
          </Col>
        </Row>
      </div>

      {/* Barre d'actions et filtres */}
      <Card className="modern-card mb-6">
        <Row gutter={[16, 16]} align="middle">
          <Col flex="auto">
            <Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                className="btn-primary"
                onClick={() => showModal()}
              >
                Nouveau Profil
              </Button>
              <Button
                icon={<KeyOutlined />}
                onClick={() => navigate('/profile/permissions')}
              >
                Gérer Permissions
              </Button>
              <Button
                icon={<SettingOutlined />}
                onClick={() => navigate('/profile/settings')}
              >
                Paramètres Avancés
              </Button>
            </Space>
          </Col>
          <Col>
            <Space size="middle" wrap>
              <Input
                placeholder="Rechercher utilisateur..."
                prefix={<SearchOutlined />}
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                allowClear
                className="w-48"
              />
              <Select
                placeholder="Rôle"
                value={filters.role}
                onChange={(value) => handleFilterChange('role', value)}
                allowClear
                className="w-40"
              >
                {roleOptions.map(role => (
                  <Option key={role.value} value={role.value}>{role.label}</Option>
                ))}
              </Select>
              <Select
                placeholder="Statut"
                value={filters.status}
                onChange={(value) => handleFilterChange('status', value)}
                allowClear
                className="w-40"
              >
                {statusOptions.map(status => (
                  <Option key={status} value={status}>{status}</Option>
                ))}
              </Select>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Tableau des profils */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden modern-card">
        {/* En-tête compact */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
          <div className="flex items-center">
            <UserOutlined className="text-gray-400 mr-2" />
            <Text className="text-gray-700 font-medium">Profils utilisateurs</Text>
          </div>
          <Text className="text-sm text-gray-500">
            {filteredProfiles.length} utilisateurs • {stats.active} actifs
          </Text>
        </div>

        {/* Tableau compact */}
        <div className="overflow-x-auto">
          <Table
            columns={columns.map(col => ({
              ...col,
              className: 'py-2'
            }))}
            dataSource={paginatedData}
            rowKey="id"
            pagination={false}
            loading={loading}
            size="small"
            className="[&_.ant-table-thead>tr>th]:bg-green-700 [&_.ant-table-thead>tr>th]:py-3 [&_.ant-table-thead>tr>th]:font-medium [&_.ant-table-thead>tr>th]:text-white [&_.ant-table-tbody>tr>td]:py-3"
            rowClassName="border-b border-gray-100 hover:bg-green-50"
          />
        </div>

        {/* Pied de tableau */}
        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <Text className="text-sm text-gray-600">
                <span className="font-semibold">Total profils :</span> {filteredProfiles.length}
              </Text>
              <Divider type="vertical" />
              <Text className="text-sm text-gray-600">
                <span className="font-semibold">Administrateurs :</span> 
                <span className="ml-1 font-bold text-red-600">{stats.admins}</span>
              </Text>
              <Divider type="vertical" />
              <Text className="text-sm text-gray-600">
                <span className="font-semibold">Managers :</span> 
                <span className="ml-1 font-bold text-orange-600">{stats.managers}</span>
              </Text>
              <Divider type="vertical" />
              <Text className="text-sm text-gray-600">
                <span className="font-semibold">Actifs :</span> 
                <span className="ml-1 font-bold text-green-600">{stats.active}</span>
              </Text>
            </div>
            
            <div className="flex items-center space-x-2">
              <Button
                size="small"
                icon={<LeftOutlined />}
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              />
              <Text className="text-sm">
                Page {currentPage} sur {Math.ceil(filteredProfiles.length / pageSize)}
              </Text>
              <Button
                size="small"
                icon={<RightOutlined />}
                disabled={currentPage === Math.ceil(filteredProfiles.length / pageSize)}
                onClick={() => setCurrentPage(currentPage + 1)}
              />
            </div>
          </div>
        </div>

        {/* Pagination détaillée */}
        <div className="px-6 py-4 border-t border-gray-200 bg-white">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="text-center sm:text-left">
              <Text className="text-gray-500 text-sm">
                Affichage <span className="font-semibold text-gray-700">
                  {Math.min((currentPage - 1) * pageSize + 1, filteredProfiles.length)}-{Math.min(currentPage * pageSize, filteredProfiles.length)}
                </span> sur <span className="font-semibold text-primary-500">{filteredProfiles.length}</span> profils
              </Text>
            </div>
            
            <div className="flex items-center space-x-4">
              <Select
                value={pageSize}
                onChange={(value) => setPageSize(value)}
                size="small"
                className="w-28"
              >
                <Option value={5}>5 éléments/page</Option>
                <Option value={10}>10 éléments/page</Option>
                <Option value={20}>20 éléments/page</Option>
                <Option value={50}>50 éléments/page</Option>
              </Select>
              
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={filteredProfiles.length}
                onChange={(page, size) => {
                  setCurrentPage(page);
                  setPageSize(size);
                }}
                showSizeChanger={false}
                showQuickJumper
                size="small"
                className="[&_.ant-pagination-item]:rounded-full [&_.ant-pagination-item-active]:bg-green-600 [&_.ant-pagination-item-active]:border-green-600 [&_.ant-pagination-item-active>a]:text-white"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Modal pour ajouter/modifier un profil */}
      <Modal
        title={editingProfile ? "Modifier le Profil" : "Nouveau Profil"}
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={700}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          className="modern-form"
          onFinish={handleSubmit}
        >
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="fullName"
                label="Nom complet"
                rules={[{ required: true, message: 'Veuillez saisir le nom complet' }]}
              >
                <Input 
                  placeholder="Ex: John Doe" 
                  prefix={<UserOutlined />}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="username"
                label="Nom d'utilisateur"
                rules={[
                  { required: true, message: "Veuillez choisir un nom d'utilisateur" },
                  { min: 3, message: 'Minimum 3 caractères' }
                ]}
              >
                <Input 
                  placeholder="Ex: johndoe" 
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Veuillez saisir l\'email' },
                  { type: 'email', message: 'Email invalide' }
                ]}
              >
                <Input 
                  placeholder="Ex: john@latyr.com" 
                  type="email"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Téléphone"
              >
                <Input 
                  placeholder="Ex: +221 77 123 45 67" 
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="department"
                label="Département"
              >
                <Select placeholder="Sélectionnez le département">
                  <Option value="Administration">Administration</Option>
                  <Option value="Commercial">Commercial</Option>
                  <Option value="Logistique">Logistique</Option>
                  <Option value="Finance">Finance</Option>
                  <Option value="Technique">Technique</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="role"
                label="Rôle initial"
                rules={[{ required: true, message: 'Veuillez sélectionner un rôle' }]}
                initialValue="user"
              >
                <Select placeholder="Sélectionnez un rôle">
                  {roleOptions.map(role => (
                    <Option key={role.value} value={role.value}>
                      <Tag color={role.color}>{role.label}</Tag>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Alert
            message="Informations importantes"
            description="Le mot de passe sera généré automatiquement et envoyé par email à l'utilisateur. L'utilisateur devra le changer à sa première connexion."
            type="info"
            showIcon
            className="mb-4"
          />

          <div className="text-right mt-6">
            <Space>
              <Button onClick={handleCancel}>
                Annuler
              </Button>
              <Button type="primary" htmlType="submit" loading={loading} className="btn-primary">
                {editingProfile ? 'Modifier' : 'Créer le profil'}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* Modal pour gérer rôles et permissions */}
      <Modal
        title={`Gérer rôles et permissions - ${editingProfile?.fullName}`}
        open={isRoleModalVisible}
        onCancel={handleRoleCancel}
        footer={null}
        width={800}
        destroyOnClose
      >
        <Form
          form={roleForm}
          layout="vertical"
          className="modern-form"
          onFinish={handleRoleSubmit}
          onValuesChange={(changedValues) => {
            if (changedValues.role) {
              // Pré-remplir les permissions selon le rôle sélectionné
              const defaultPermissions = getRolePermissions(changedValues.role);
              roleForm.setFieldsValue({ permissions: defaultPermissions });
            }
          }}
        >
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="role"
                label="Rôle"
                rules={[{ required: true, message: 'Veuillez sélectionner un rôle' }]}
              >
                <Select placeholder="Sélectionnez un rôle">
                  {roleOptions.map(role => (
                    <Option key={role.value} value={role.value}>
                      <div className="flex items-center gap-2">
                        <Tag color={role.color}>{role.label}</Tag>
                      </div>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="permissions"
            label="Permissions"
          >
            <Select
              mode="multiple"
              placeholder="Sélectionnez les permissions"
              allowClear
              style={{ width: '100%' }}
            >
              {availablePermissions.map(perm => (
                <Option key={perm.key} value={perm.key}>
                  {perm.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Card title="Description des rôles" size="small" className="mb-4">
            <Row gutter={[16, 16]}>
              {roleOptions.map(role => (
                <Col span={12} key={role.value}>
                  <div className="p-3 border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Tag color={role.color}>{role.label}</Tag>
                    </div>
                    {role.value === 'admin' && (
                      <Text type="secondary" className="text-xs">
                        Accès complet à toutes les fonctionnalités
                      </Text>
                    )}
                    {role.value === 'manager' && (
                      <Text type="secondary" className="text-xs">
                        Accès commercial et reporting
                      </Text>
                    )}
                    {role.value === 'user' && (
                      <Text type="secondary" className="text-xs">
                        Accès opérationnel limité
                      </Text>
                    )}
                    {role.value === 'viewer' && (
                      <Text type="secondary" className="text-xs">
                        Accès en lecture seule aux rapports
                      </Text>
                    )}
                  </div>
                </Col>
              ))}
            </Row>
          </Card>

          <div className="text-right mt-6">
            <Space>
              <Button onClick={handleRoleCancel}>
                Annuler
              </Button>
              <Button type="primary" htmlType="submit" loading={loading} className="btn-primary">
                Enregistrer les modifications
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default UserPage;